import * as turf from "@turf/turf"
import type { Feature, FeatureCollection, Point, Polygon } from "geojson"
import { memo, useCallback, useMemo, useState } from "react"

import {
	DRAW_CIRCLE_LAYER_PAINT,
	DRAW_FILL_LAYER_PAINT,
	DRAW_LINE_LAYER_PAINT,
	LINE_STRING_GEOMETRY_TYPE_FILTER,
	POINT_GEOMETRY_TYPE_FILTER,
	POLYGON_GEOMETRY_TYPE_FILTER,
} from "../constants"
import { MapSlotLayerId } from "../enums"
import { useCurrentMapEvent } from "../hooks"
import type { MapEventHandler } from "../typings"
import { Layer, Source } from "./memo"

const RECTANGLE_DRAWER_SOURCE_ID = "rectangle-drawer-source"

enum RectangleDrawerLayedId {
	CIRCLE = "rectangle-drawer-circle-layer",
	LINE = "rectangle-drawer-line-layer",
	FILL = "rectangle-drawer-fill-layer",
}

interface RectangleDrawerProps {
	onComplete?: (rectangle: Polygon) => void
}

export const RectangleDrawer = memo<RectangleDrawerProps>((props) => {
	const { onComplete } = props

	const [firstPoint, setFirstPoint] = useState<Feature<Point> | null>(null)
	const [secondPoint, setSecondPoint] = useState<Feature<Point> | null>(null)

	const reset = useCallback(() => {
		setFirstPoint(null)
		setSecondPoint(null)
	}, [])

	const handleMapClick = useCallback<MapEventHandler<"click">>(
		(e) => {
			const { lngLat } = e

			const point = turf.point(lngLat.toArray())

			if (!firstPoint || !secondPoint) {
				setFirstPoint(point)
				setSecondPoint(point)
			} else {
				onComplete?.(
					turf.envelope(turf.multiPoint([firstPoint.geometry.coordinates, secondPoint.geometry.coordinates]))
						.geometry,
				)
				reset()
			}
		},
		[firstPoint, onComplete, reset, secondPoint],
	)

	const handleMouseMove = useCallback<MapEventHandler<"mousemove">>(
		(e) => {
			const { lngLat } = e

			if (!firstPoint || !secondPoint) return

			const point = turf.point(lngLat.toArray())
			setSecondPoint(point)
		},
		[firstPoint, secondPoint],
	)

	useCurrentMapEvent("click", handleMapClick)
	useCurrentMapEvent("mousemove", handleMouseMove)

	const geojson = useMemo<FeatureCollection>(() => {
		const features: Feature[] = []

		if (firstPoint && secondPoint) {
			features.push(firstPoint, secondPoint)

			const polygon = turf.envelope(
				turf.multiPoint([firstPoint.geometry.coordinates, secondPoint.geometry.coordinates]),
			)

			// @ts-expect-error REASON: sector is a singular Polygon feature
			features.push(turf.polygonToLine(polygon))
			features.push(polygon)
		}

		return turf.featureCollection(features)
	}, [firstPoint, secondPoint])

	return (
		<Source id={RECTANGLE_DRAWER_SOURCE_ID} type="geojson" data={geojson}>
			<Layer
				id={RectangleDrawerLayedId.CIRCLE}
				type="circle"
				paint={DRAW_CIRCLE_LAYER_PAINT}
				filter={POINT_GEOMETRY_TYPE_FILTER}
				beforeId={MapSlotLayerId.DRAW}
			/>
			<Layer
				id={RectangleDrawerLayedId.LINE}
				type="line"
				paint={DRAW_LINE_LAYER_PAINT}
				filter={LINE_STRING_GEOMETRY_TYPE_FILTER}
				beforeId={RectangleDrawerLayedId.CIRCLE}
			/>
			<Layer
				id={RectangleDrawerLayedId.FILL}
				type="fill"
				paint={DRAW_FILL_LAYER_PAINT}
				filter={POLYGON_GEOMETRY_TYPE_FILTER}
				beforeId={RectangleDrawerLayedId.LINE}
			/>
		</Source>
	)
})
RectangleDrawer.displayName = "RectangleDrawer"
